import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { LogOut, ChevronDown, Settings as SettingsIcon, Shield, Bell } from "lucide-react";

/* ================================================================
   Nexus One TopBar — Sprint I-3 polish
   Glassy backdrop, gradient A-mark, plan chip, avatar menu.
   ================================================================ */

export default function TopBar({ user, company, onLogout }) {
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const onDoc = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const initials = (user?.full_name || "?")
        .split(" ")
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase())
        .join("");

    const isOwner = user?.role === "platform_owner";
    const isLifetimeOwner = user?.is_lifetime_owner === true;
    const planLabel = (company?.plan || "starter").replace("_", " ");

    return (
        <header
            className="h-14 flex-shrink-0 flex items-center justify-between px-6 relative z-20"
            style={{
                background: "rgba(10, 10, 26, 0.72)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                borderBottom: "1px solid rgba(139, 92, 246, 0.14)",
            }}
        >
            <div className="flex items-center gap-3 min-w-0">
                <Link to="/app" className="flex items-center gap-2.5" data-testid="topbar-logo">
                    <span className="atlas-logo-mark text-[14px] h-8 min-w-[32px]">N</span>
                    <span
                        className="text-[14.5px] font-bold tracking-tight"
                        style={{ color: "var(--atlas-text-primary)", letterSpacing: "-0.02em" }}
                    >
                        Nexus One
                    </span>
                </Link>
                <span className="w-px h-5" style={{ background: "var(--atlas-border-strong)" }} />
                <div
                    className="text-[13px] font-semibold tracking-tight truncate max-w-[240px]"
                    style={{ color: "var(--atlas-text-secondary)" }}
                    data-testid="topbar-company-name"
                >
                    {company?.name || "Workspace"}
                </div>
                <span
                    className="wfos-pill capitalize"
                    data-testid="topbar-plan-pill"
                    style={{
                        background: "var(--atlas-primary-muted)",
                        color: "var(--atlas-primary-hover)",
                        borderColor: "rgba(139,92,246,0.4)",
                    }}
                >
                    <span className="wfos-dot" /> {planLabel}
                </span>
            </div>

            <div className="flex items-center gap-2">
                {isLifetimeOwner && (
                    <span
                        data-testid="lifetime-owner-badge"
                        title="Platform owner — perpetual all-features access"
                        className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                        style={{
                            background:
                                "linear-gradient(135deg, rgba(167,139,250,0.20) 0%, rgba(124,58,237,0.12) 100%)",
                            color: "#c4b5fd",
                            borderColor: "rgba(167,139,250,0.55)",
                            boxShadow: "0 0 12px -4px var(--atlas-primary-glow)",
                        }}
                    >
                        <Shield className="w-3 h-3" strokeWidth={2} />
                        Platform Owner · Lifetime
                    </span>
                )}
                {isOwner && !isLifetimeOwner && (
                    <span
                        data-testid="topbar-owner-pill"
                        className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                        style={{
                            background: "var(--atlas-warning-muted)",
                            color: "#fbbf24",
                            borderColor: "rgba(245,158,11,0.45)",
                        }}
                    >
                        <Shield className="w-3 h-3" strokeWidth={2} /> Owner
                    </span>
                )}

                <button
                    className="w-9 h-9 rounded-full grid place-items-center atlas-btn-ghost relative"
                    title="Notifications"
                    data-testid="topbar-notifications"
                >
                    <Bell className="w-3.5 h-3.5" strokeWidth={2} />
                    <span
                        className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full"
                        style={{
                            background: "var(--atlas-primary)",
                            boxShadow: "0 0 6px var(--atlas-primary-glow)",
                            animation: "atlas-orb-pulse 2.4s ease-in-out infinite",
                        }}
                    />
                </button>

                <div className="relative" ref={menuRef}>
                    <button
                        onClick={() => setOpen((v) => !v)}
                        data-testid="topbar-user"
                        className="inline-flex items-center gap-2 rounded-lg atlas-btn-ghost px-2 h-9 text-sm"
                    >
                        <span
                            className="w-6 h-6 rounded-full grid place-items-center text-[10.5px] font-bold text-white"
                            style={{
                                background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2)",
                            }}
                        >
                            {initials || "?"}
                        </span>
                        <span
                            className="max-w-[140px] truncate"
                            style={{ color: "var(--atlas-text-primary)" }}
                        >
                            {user?.full_name || "You"}
                        </span>
                        <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} style={{ color: "var(--atlas-text-muted)" }} />
                    </button>
                    {open && (
                        <div
                            className="absolute right-0 mt-2 w-64 rounded-xl p-1.5 z-50 atlas-card"
                            style={{ boxShadow: "0 12px 32px rgba(0,0,0,0.5)" }}
                        >
                            <div className="px-2.5 py-2">
                                <div className="text-sm font-semibold truncate" style={{ color: "var(--atlas-text-primary)" }}>{user?.full_name}</div>
                                <div className="text-[11px] truncate" style={{ color: "var(--atlas-text-muted)" }}>{user?.email}</div>
                            </div>
                            <div className="h-px my-1" style={{ background: "var(--atlas-border-strong)" }} />
                            <Link
                                to="/app/settings"
                                onClick={() => setOpen(false)}
                                data-testid="topbar-settings-link"
                                className="w-full inline-flex items-center gap-2 rounded-md px-2 h-8 text-[13px]"
                                style={{ color: "var(--atlas-text-secondary)" }}
                            >
                                <SettingsIcon className="w-3.5 h-3.5" strokeWidth={2} /> Settings & VIP code
                            </Link>
                            {isOwner && (
                                <Link
                                    to="/app/platform"
                                    onClick={() => setOpen(false)}
                                    data-testid="topbar-platform-link"
                                    className="w-full inline-flex items-center gap-2 rounded-md px-2 h-8 text-[13px]"
                                    style={{ color: "#fbbf24" }}
                                >
                                    <Shield className="w-3.5 h-3.5" strokeWidth={2} /> Platform Owner Console
                                </Link>
                            )}
                            <div className="h-px my-1" style={{ background: "var(--atlas-border-strong)" }} />
                            <button
                                onClick={() => { setOpen(false); onLogout(); }}
                                data-testid="topbar-logout"
                                className="w-full inline-flex items-center gap-2 rounded-md px-2 h-8 text-[13px]"
                                style={{ color: "var(--atlas-text-secondary)" }}
                            >
                                <LogOut className="w-3.5 h-3.5" strokeWidth={2} /> Sign out
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}
